/**
 * BoardHeatmap Component
 * Visual heatmap showing landing frequency on the board
 */

import { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';
import { gridPositionToSquare, getSquareCenter } from './gridHelpers';

/**
 * BoardHeatmap Component
 * @param {number} rows - Number of rows
 * @param {number} cols - Number of columns
 * @param {number} maxPixels - Maximum dimensions
 * @param {object} landingCounts - Map of square number to landing count
 * @param {Array} snakes - Snake configurations
 * @param {Array} ladders - Ladder configurations
 * @param {object} snakeHits - Map of snake key to hit count (optional)
 * @param {object} ladderHits - Map of ladder key to hit count (optional)
 * @param {boolean} simpleArrows - Use simple straight arrows instead of styled ones
 */
export const BoardHeatmap = ({
    rows = 10,
    cols = 10,
    maxPixels = 400,
    landingCounts = {},
    snakes = [],
    ladders = [],
    snakeHits = {},
    ladderHits = {},
    simpleArrows = false
}) => {
    const containerRef = useRef(null);
    const svgRef = useRef(null);
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

    // Calculate responsive dimensions
    useEffect(() => {
        const updateDimensions = () => {
            if (containerRef.current) {
                const containerWidth = containerRef.current.clientWidth;
                const size = Math.min(containerWidth, maxPixels);
                setDimensions({ width: size, height: size });
            }
        };

        updateDimensions();
        window.addEventListener('resize', updateDimensions);
        return () => window.removeEventListener('resize', updateDimensions);
    }, [maxPixels]);

    // Draw the heatmap
    useEffect(() => {
        if (!svgRef.current || dimensions.width === 0) return;

        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove();

        const { width, height } = dimensions;
        const cellWidth = width / cols;
        const cellHeight = height / rows;
        const maxPosition = rows * cols;

        // Build sets of snake/ladder start positions for special handling
        const snakeStarts = new Set(snakes.map(s => s.start));
        const ladderStarts = new Set(ladders.map(l => l.start));
        const specialSquares = new Set([...snakeStarts, ...ladderStarts]);

        // Build a complete landing counts map (ensure all squares have values)
        const completeLandingCounts = {};
        for (let i = 1; i <= maxPosition; i++) {
            completeLandingCounts[i] = landingCounts[i] || 0;
        }

        // Calculate max landing count for color scale (excluding special squares)
        const regularCounts = Object.entries(completeLandingCounts)
            .filter(([sq]) => !specialSquares.has(parseInt(sq)))
            .map(([, count]) => count);
        const maxCount = Math.max(...regularCounts, 1);

        // Color scale (white to purple)
        const colorScale = d3.scaleSequential(d3.interpolatePurples)
            .domain([0, maxCount]);

        // Create group for the grid
        const gridGroup = svg.append('g').attr('class', 'heatmap-grid');

        // Draw grid cells with heatmap colors
        for (let row = 0; row < rows; row++) {
            for (let col = 0; col < cols; col++) {
                const x = col * cellWidth;
                const y = row * cellHeight;
                const squareNumber = gridPositionToSquare(row, col, rows, cols);
                const count = completeLandingCounts[squareNumber];
                const isSpecial = specialSquares.has(squareNumber);

                // Draw cell rectangle with heatmap color
                gridGroup.append('rect')
                    .attr('x', x)
                    .attr('y', y)
                    .attr('width', cellWidth)
                    .attr('height', cellHeight)
                    .attr('fill', isSpecial ? '#FAFAFA' : (count > 0 ? colorScale(count) : '#FAFAFA'))
                    .attr('stroke', '#E5E7EB')
                    .attr('stroke-width', 0.5);

                // Add square number (smaller, more subtle)
                const fontSize = Math.max(6, Math.min(10, cellWidth / 5));
                gridGroup.append('text')
                    .attr('x', x + 2)
                    .attr('y', y + fontSize + 1)
                    .attr('font-size', fontSize)
                    .attr('fill', count > maxCount * 0.5 && !isSpecial ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.5)')
                    .attr('font-family', 'sans-serif')
                    .text(squareNumber);

                // Show count in center only for non-special squares
                if (!isSpecial) {
                    const countFontSize = Math.max(8, cellWidth / 4);
                    gridGroup.append('text')
                        .attr('x', x + cellWidth / 2)
                        .attr('y', y + cellHeight / 2 + countFontSize / 3)
                        .attr('text-anchor', 'middle')
                        .attr('font-size', countFontSize)
                        .attr('font-weight', count > 0 ? 'bold' : 'normal')
                        .attr('fill', count === 0 ? 'rgba(0,0,0,0.25)' : (count > maxCount * 0.5 ? 'white' : '#374151'))
                        .text(count >= 1000 ? `${Math.round(count/1000)}k` : count);
                }
            }
        }

        // Create group for snakes and ladders overlay
        const snakeLadderGroup = svg.append('g').attr('class', 'snakes-ladders-overlay');

        // Get max hits for circle/thickness scaling
        const maxSnakeHits = Math.max(...Object.values(snakeHits), 1);
        const maxLadderHits = Math.max(...Object.values(ladderHits), 1);
        const minCircleRadius = Math.max(4, cellWidth / 10);
        const maxCircleRadius = Math.max(8, cellWidth / 4);

        // Draw arrows for ladders with hit frequency circles
        ladders.forEach(ladder => {
            const startPos = getSquareCenter(ladder.start, rows, cols, cellWidth, cellHeight);
            const endPos = getSquareCenter(ladder.end, rows, cols, cellWidth, cellHeight);
            const key = `${ladder.start}-${ladder.end}`;
            const hits = ladderHits[key] || 0;
            const hitRatio = hits / maxLadderHits;

            if (simpleArrows) {
                const thickness = 1.5 + hitRatio * 2.5;

                // Arrow line
                snakeLadderGroup.append('line')
                    .attr('x1', startPos.x)
                    .attr('y1', startPos.y)
                    .attr('x2', endPos.x)
                    .attr('y2', endPos.y)
                    .attr('stroke', '#F1C40F')
                    .attr('stroke-width', thickness)
                    .attr('stroke-linecap', 'round')
                    .attr('opacity', 0.8);

                // Arrowhead at end
                const angle = Math.atan2(endPos.y - startPos.y, endPos.x - startPos.x);
                const arrowSize = 6 + thickness;
                snakeLadderGroup.append('polygon')
                    .attr('points', `0,-${arrowSize/2} ${arrowSize},0 0,${arrowSize/2}`)
                    .attr('transform', `translate(${endPos.x}, ${endPos.y}) rotate(${angle * 180 / Math.PI})`)
                    .attr('fill', '#F1C40F')
                    .attr('opacity', 0.8);

                // Circle at start position showing hit frequency
                const circleRadius = minCircleRadius + hitRatio * (maxCircleRadius - minCircleRadius);
                snakeLadderGroup.append('circle')
                    .attr('cx', startPos.x)
                    .attr('cy', startPos.y)
                    .attr('r', circleRadius)
                    .attr('fill', '#F1C40F')
                    .attr('stroke', '#D4A30E')
                    .attr('stroke-width', 1)
                    .attr('opacity', 0.9);
            } else {
                const thickness = 1 + hitRatio * 3;
                snakeLadderGroup.append('line')
                    .attr('x1', startPos.x)
                    .attr('y1', startPos.y)
                    .attr('x2', endPos.x)
                    .attr('y2', endPos.y)
                    .attr('stroke', '#F1C40F')
                    .attr('stroke-width', thickness)
                    .attr('stroke-linecap', 'round')
                    .attr('opacity', 0.7);
            }
        });

        // Draw arrows for snakes with hit frequency circles
        snakes.forEach(snake => {
            const startPos = getSquareCenter(snake.start, rows, cols, cellWidth, cellHeight);
            const endPos = getSquareCenter(snake.end, rows, cols, cellWidth, cellHeight);
            const key = `${snake.start}-${snake.end}`;
            const hits = snakeHits[key] || 0;
            const hitRatio = hits / maxSnakeHits;

            if (simpleArrows) {
                const thickness = 1.5 + hitRatio * 2.5;

                // Arrow line
                snakeLadderGroup.append('line')
                    .attr('x1', startPos.x)
                    .attr('y1', startPos.y)
                    .attr('x2', endPos.x)
                    .attr('y2', endPos.y)
                    .attr('stroke', '#A26ECC')
                    .attr('stroke-width', thickness)
                    .attr('stroke-linecap', 'round')
                    .attr('opacity', 0.8);

                // Arrowhead at end
                const angle = Math.atan2(endPos.y - startPos.y, endPos.x - startPos.x);
                const arrowSize = 6 + thickness;
                snakeLadderGroup.append('polygon')
                    .attr('points', `0,-${arrowSize/2} ${arrowSize},0 0,${arrowSize/2}`)
                    .attr('transform', `translate(${endPos.x}, ${endPos.y}) rotate(${angle * 180 / Math.PI})`)
                    .attr('fill', '#A26ECC')
                    .attr('opacity', 0.8);

                // Circle at start position showing hit frequency
                const circleRadius = minCircleRadius + hitRatio * (maxCircleRadius - minCircleRadius);
                snakeLadderGroup.append('circle')
                    .attr('cx', startPos.x)
                    .attr('cy', startPos.y)
                    .attr('r', circleRadius)
                    .attr('fill', '#A26ECC')
                    .attr('stroke', '#8B5CB3')
                    .attr('stroke-width', 1)
                    .attr('opacity', 0.9);
            } else {
                const thickness = 1 + hitRatio * 3;
                snakeLadderGroup.append('line')
                    .attr('x1', startPos.x)
                    .attr('y1', startPos.y)
                    .attr('x2', endPos.x)
                    .attr('y2', endPos.y)
                    .attr('stroke', '#A26ECC')
                    .attr('stroke-width', thickness)
                    .attr('stroke-linecap', 'round')
                    .attr('opacity', 0.7);
            }
        });

    }, [dimensions, rows, cols, landingCounts, snakes, ladders, snakeHits, ladderHits, simpleArrows]);

    return (
        <div className="w-full" style={{ maxWidth: maxPixels }}>
            <div ref={containerRef} className="w-full">
                <svg
                    ref={svgRef}
                    width={dimensions.width}
                    height={dimensions.height}
                    className="border border-gray-200 rounded"
                />
            </div>

            {/* Combined legend row */}
            <div className="flex items-center justify-center flex-wrap gap-x-3 gap-y-1 mt-2 text-xs text-gray-500">
                <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded" style={{ background: d3.interpolatePurples(0.2) }} />
                    <span>Low</span>
                </div>
                <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded" style={{ background: d3.interpolatePurples(1) }} />
                    <span>High</span>
                </div>
                <span className="text-gray-300">|</span>
                <div className="flex items-center gap-1">
                    <div className="w-2.5 h-2.5 rounded-full bg-badger-purple" />
                    <span>Snake</span>
                </div>
                <div className="flex items-center gap-1">
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                    <span>Ladder</span>
                </div>
            </div>
        </div>
    );
};

export default BoardHeatmap;
